import {
  memo,
  type MouseEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  AcesCarousel,
  BostonAsianFoodCarousel,
  ByChaeCarousel,
  CAMHoustonCarousel,
  ComedyStoreCarousel,
  DopeEntertainmentCarousel,
  HarvardStCommonsCarousel,
  IMOCarousel,
  JellyFamCarousel,
  LosGlobosCarousel,
  MassAICoalitionCarousel,
  MrDinnerPartyCarousel,
  OldDominionCarousel,
  RestlessRoadCarousel,
  SeaavCarousel,
  StoopDatingCarousel,
  TabuCarousel,
  TheLabelleFoundationCarousel,
  TuftsCarousel,
} from "../../assets/LandingPage/Carousel/TopCarousel/topCarouselIndex";
import {
  ASAPRockyCarousel,
  AnaVelezCarousel,
  BretClancyCarousel,
  ChristyLamCarousel,
  ClaireSawaCarousel,
  ColbyMartelCarousel,
  EmilyCoffmanCarousel,
  GraceJenkinsCarousel,
  GraciePACarousel,
  HelaineZhaoCarousel,
  HollywoodHinoCarousel,
  HorchataSotoCarousel,
  JakeDoggettCarousel,
  JordanFloresCarousel,
  JuliaBelkinCarousel,
  KateBergmanCarousel,
  LeonChristianCarousel,
  MadiBeumeeCarousel,
  MariaComstockCarousel,
  MattLyonsCarousel,
  MattRossiCarousel,
  MollyJCurleyCarousel,
  NicoleSalibaCarousel,
  NickToumaCarousel,
  NoahKatzCarousel,
  OliviaZhangCarousel,
  SeanFontaineCarousel,
  SheenaMelwaniCarousel,
  TRIDCarousel,
  TeriIjeomaCarousel,
  ValentinaMussiCarousel,
} from "../../assets/LandingPage/Carousel/BottomCarousel/bottomCarouselIndex";
import { Colors } from "../../utils/colors";
import { getShareDomain } from "../../utils/shareLink";

const DRAG_START_THRESHOLD_PX = 5;

type CarouselEntry = {
  image: string;
  title: string;
  subtext?: string;
  // Markit username for the landing page - possible that usernames are changed so keep in mind
  username?: string;
};

const TOP_ROW_ENTRIES: CarouselEntry[] = [
  {
    image: CAMHoustonCarousel,
    title: "CAM Houston",
    subtext: "Contemporary art museum showcasing global modern art",
    username: "camhouston",
  },
  {
    image: ComedyStoreCarousel,
    title: "The Comedy Store",
    subtext: "The greatest stand-up\ncomedy club in the world",
    username: "thecomedystore",
  },
  {
    image: SeaavCarousel,
    title: "Seaav",
    subtext: "Activewear brand focused\non sustainability",
    username: "seaav",
  },
  {
    image: MassAICoalitionCarousel,
    title: "Massachusetts AI Coalition",
    subtext:
      "Coalition uniting Massachusetts' AI\nfounders, investors, and builders",
    username: "massaicoalition",
  },
  {
    image: ByChaeCarousel,
    title: "byChae",
    subtext: "Viral Boston matcha brand on\nNewbury St, 65K+ followers",
    username: "bychae",
  },
  {
    image: StoopDatingCarousel,
    title: "Stoop Dating",
    subtext: "Popular Boston-based, social\nmedia-driven dating show",
    username: "stoopdating",
  },
  {
    image: AcesCarousel,
    title: "ACES",
    subtext: "Basketball, media, clothing brand &\nNBA & Google Pixel partner",
    username: "aces",
  },
  {
    image: JellyFamCarousel,
    title: "Jelly Fam",
    subtext: "NYC Basketball team and official\nmember of Overtime Elite",
    username: "oslbasketball",
  },
  {
    image: OldDominionCarousel,
    title: "Old Dominion",
    subtext:
      "Multi-platinum country band with 2B+ streams & sold-out arena tours",
    username: "olddominion",
  },
  {
    image: RestlessRoadCarousel,
    title: "Restless Road",
    subtext:
      "Breakout country trio with 300M+\nstreams, & sold-out tours worldwide",
  },
  {
    image: DopeEntertainmentCarousel,
    title: "Dope Entertainment",
    subtext:
      "Boston nightlife & events company\nbehind weekly parties and pop-ups",
    username: "dope",
  },
  {
    image: TabuCarousel,
    title: "TABU BOSTON",
    subtext:
      "Boston nightlife brand hosting ultra\nlounge parties & pop-up events",
    username: "tabu",
  },
  {
    image: MrDinnerPartyCarousel,
    title: "MrDinnerParty",
    subtext:
      "Boston social dining & nightlife brand hosting weekly Saturday parties",
    username: "juba",
  },
  {
    image: BostonAsianFoodCarousel,
    title: "Boston Asian Food Network",
    subtext:
      "Home of Boston Asian Restaurant Week, celebrating Asian food & culture",
    username: "bostonasianfoodnetwork",
  },
  {
    image: HarvardStCommonsCarousel,
    title: "Harvard Street Commons",
    subtext: "Space for Builders, Hackers,\nand Creators Building the Future",
    username: "harvardstcommons",
  },
  {
    image: TuftsCarousel,
    title: "Tufts University",
    subtext:
      "Boston based research university,\nleader in innovation, & global impact",
    username: "derbyecenter",
  },
  {
    image: LosGlobosCarousel,
    title: "Los Globos",
    subtext: "Iconic nightclub & live music\nvenue in LA's Silver Lake",
    username: "laevents",
  },
  {
    image: IMOCarousel,
    title: "International Margarita Org",
    subtext:
      "Organization behind margarita festivals celebrated across U.S. cities",
    username: "imo",
  },
  {
    image: TheLabelleFoundationCarousel,
    title: "The Labelle Foundation",
    subtext:
      "A 501(c)(3) Animal Rescue in LA\nfinding fur-ever homes for loving dogs",
    username: "thelabellefoundation",
  },
];

const BOTTOM_ROW_ENTRIES: CarouselEntry[] = [
  {
    image: LeonChristianCarousel,
    title: "Leon Christian",
    subtext:
      "BU track alum, influencer and pro sprinter aiming to be the fastest man of all time",
    username: "wannagetfaster",
  },
  {
    image: ASAPRockyCarousel,
    title: "A$AP Rocky",
    subtext: "Artist, businessman,\nfashion killer",
    username: "asaprocky",
  },
  {
    image: ClaireSawaCarousel,
    title: "Claire Sawa",
    subtext:
      "Boston-based creator known for viral jelly & food art, 600K+ followers",
    username: "clairesawa",
  },
  {
    image: OliviaZhangCarousel,
    title: "Olivia Zhang",
    subtext:
      "Harvard student creator, Forbes 30\nUnder 30, founder of Cancer Kids First",
    username: "livviazhang",
  },
  {
    image: SheenaMelwaniCarousel,
    title: "Sheena Melwani",
    subtext:
      "Singer-songwriter & family creator, host of @sheenainterrupted, 18M+ followers",
    username: "sheenamelwani",
  },
  {
    image: TRIDCarousel,
    title: "TRID",
    subtext:
      "Comedy & family creator known as\nThe Real Indian Dad, 6M+ followers",
    username: "trid",
  },
  {
    image: MattRossiCarousel,
    title: "Matt Rossi",
    subtext:
      "Lifestyle + fitness influencer with a tiny wiener (dog), host of @thescariespod",
    username: "thescaries",
  },
  {
    image: JordanFloresCarousel,
    title: "Jordan Flores",
    subtext:
      "Language & writing creator known as Registered Wordsmith, 750K+ followers",
    username: "jordanblores",
  },
  {
    image: MariaComstockCarousel,
    title: "Maria Comstock",
    subtext:
      "Political & dating humor creator, interviews her retired-spy dad, 2M+ followers",
    username: "mariacomstock",
  },
  {
    image: ValentinaMussiCarousel,
    title: "Valentina Mussi",
    subtext:
      "Pastry chef influencer with 2M+ followers. Founded matcha brand produced in Japan",
    username: "sweetportfolio",
  },
  {
    image: MattLyonsCarousel,
    title: "Matt Lyons",
    subtext:
      "Boston comedy creator known for\noutdoorsy hiking humor, 1.5M+ followers",
    username: "mattslyon",
  },
  {
    image: KateBergmanCarousel,
    title: "Kate Bergman",
    subtext:
      "Founder of @bucketlistboston, Boston's things-to-do account, 270K+ followers",
    username: "bucketlistboston",
  },
  {
    image: ChristyLamCarousel,
    title: "Christy Lam",
    subtext: "30K+ tech content creator,\nSWE @ Dell, ex-NVIDIA engineer",
    username: "christy",
  },
  {
    image: HelaineZhaoCarousel,
    title: "Helaine Zhao",
    subtext: "Harvard lifestyle & beauty creator\nwith 330K+ TikTok followers",
    username: "helainezhao",
  },
  {
    image: EmilyCoffmanCarousel,
    title: "Emily Coffman",
    subtext:
      "Boston lifestyle creator behind Boston With Emily, spotlighting local events",
    username: "bostonwithemily",
  },
  {
    image: TeriIjeomaCarousel,
    title: "Teri Ijeoma",
    subtext:
      "NYT bestseller and globally recognized financial creator and educator",
    username: "tradeandtravel",
  },
  {
    image: HollywoodHinoCarousel,
    title: "King Hino",
    subtext:
      "Celebrity boxing & fitness trainer for Drake, Kevin Hart, Jake Paul, & more",
    username: "kinghino",
  },
  {
    image: NicoleSalibaCarousel,
    title: "Nicole Saliba",
    subtext: "Boston fashion creator & founder\nof Let's Get Dressed Boston",
    username: "letsgetdressed",
  },
  {
    image: MadiBeumeeCarousel,
    title: "Madi Beumee",
    subtext: "Social Media Strategist,\nFounder of The Roundup",
    username: "madibeumee",
  },
  {
    image: SeanFontaineCarousel,
    title: "Sean Fontaine",
    subtext: "Fitness & nutrition coach behind\nFontyFitness, 800K+ followers",
    username: "fontyfitness",
  },
  {
    image: JakeDoggettCarousel,
    title: "Jake Doggett",
    subtext:
      "Boston creator behind Salvage to Scenic, upcycling builds, 1.5M+ followers",
    username: "salvagetoscenic",
  },
  {
    image: GraciePACarousel,
    title: "Gracie PA",
    subtext: "Lifestyle influencer, NYU Stern student, founder of Jersey Lily",
    username: "jerseylily",
  },
  {
    image: GraceJenkinsCarousel,
    title: "Grace Jenkins",
    subtext:
      "Elementary teacher creator behind\nTeaching With Grace, 170K+ followers",
    username: "teaching_with_grace",
  },
  {
    image: MollyJCurleyCarousel,
    title: "Molly J Curley Farrow",
    subtext: "Amazon fashion seller & motherhood creator with 600K+ followers",
    username: "mollyj",
  },
  {
    image: AnaVelezCarousel,
    title: "Ana Velez",
    subtext:
      "Boston Latin lifestyle & mom creator, founder of By Ana Velez, 3M+ followers",
    username: "anavelez89",
  },
  {
    image: BretClancyCarousel,
    title: "Bret Clancy",
    subtext:
      "Boston Insider creator covering the city's best food, events, and things to do, 60K+ followers",
    username: "bretclancy",
  },
  {
    image: JuliaBelkinCarousel,
    title: "Julia Belkin",
    subtext: "Boston bargain hunter behind\nFreebies & More, 1M+ followers",
    username: "freebiesandmore",
  },
  {
    image: HorchataSotoCarousel,
    title: "Horchata Soto",
    subtext: "Comedy skits creator known as\nHorchata Soto, 7.5M+ followers",
    username: "horchata_soto",
  },
  {
    image: NoahKatzCarousel,
    title: "Noah Katz",
    subtext:
      "Founder of The Umpire Channel,\numpire-turned-creator, 400K+ followers",
    username: "theumpirechannel",
  },
  {
    image: ColbyMartelCarousel,
    title: "Colby Martel",
    subtext:
      "Boston street-interview creator behind 'Talking to Strangers,' 2.5M+ followers",
    username: "colbymartel",
  },
  {
    image: NickToumaCarousel,
    title: "Nick Touma",
    subtext:
      "Content creator (@nicktguy) known for viral candy art, 4M+ followers",
    username: "nicktguy",
  },
];

// Row scrolls in reverse, so reverse the entry order too to preserve the original left-to-right reading order.
const REVERSED_BOTTOM_ROW_ENTRIES = [...BOTTOM_ROW_ENTRIES].reverse();

type CarouselRowProps = {
  entries: CarouselEntry[];
  isDesktopSize: boolean;
  reverse?: boolean;
};

const CarouselRow = (props: CarouselRowProps) => {
  const { entries, isDesktopSize, reverse } = props;
  // Refs to store the logo container and scroll interval ID
  const logoContainerRef = useRef<HTMLDivElement>(null);
  const scrollIntervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const wheelResumeTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const isPointerDownRef = useRef(false);
  const isDraggingRef = useRef(false);
  const suppressClickRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  // Track active pointerId and clean up on window to prevent stuck carousel if pointer events are interrupted.
  const activePointerIdRef = useRef<number | null>(null);
  // Tracks committed wheel gesture axis; resets after inactivity.
  const wheelGestureAxisRef = useRef<"horizontal" | "vertical" | null>(null);
  const wheelGestureEndTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const getLoopDistance = useCallback((scrollContainer: HTMLDivElement) => {
    const [firstGroup, secondGroup] = Array.from(
      scrollContainer.children
    ) as HTMLElement[];

    if (firstGroup && secondGroup) {
      return secondGroup.offsetLeft - firstGroup.offsetLeft;
    }

    return scrollContainer.scrollWidth / 2;
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (scrollIntervalIdRef.current) {
      clearInterval(scrollIntervalIdRef.current);
      scrollIntervalIdRef.current = null;
    }
  }, []);

  const clearWheelResumeTimeout = useCallback(() => {
    if (wheelResumeTimeoutIdRef.current) {
      clearTimeout(wheelResumeTimeoutIdRef.current);
      wheelResumeTimeoutIdRef.current = null;
    }
  }, []);

  const normalizeScrollLeft = useCallback(
    (scrollContainer: HTMLDivElement, scrollLeft: number) => {
      const loopDistance = getLoopDistance(scrollContainer);
      if (!loopDistance) return scrollLeft;

      let normalizedScrollLeft = scrollLeft;
      while (normalizedScrollLeft < 0) {
        normalizedScrollLeft += loopDistance;
      }
      while (normalizedScrollLeft >= loopDistance) {
        normalizedScrollLeft -= loopDistance;
      }

      return normalizedScrollLeft;
    },
    [getLoopDistance]
  );

  const startAutoScroll = useCallback(() => {
    const scrollContainer = logoContainerRef.current;
    if (!scrollContainer || scrollIntervalIdRef.current) return;

    const scroll = () => {
      scrollContainer.scrollLeft = normalizeScrollLeft(
        scrollContainer,
        scrollContainer.scrollLeft + (reverse ? -1 : 1)
      );
    };

    scrollIntervalIdRef.current = setInterval(scroll, 30);
  }, [normalizeScrollLeft, reverse]);

  // Carousel animation
  useEffect(() => {
    const scrollContainer = logoContainerRef.current;
    if (!scrollContainer) return;

    // Start a reverse row with the first group's last entry at the right edge of the
    // viewport, so it reads the same as the forward row on mount, then scrolls backward.
    if (reverse) {
      const loopDistance = getLoopDistance(scrollContainer);
      scrollContainer.scrollLeft = normalizeScrollLeft(
        scrollContainer,
        loopDistance - scrollContainer.clientWidth
      );
    }

    stopAutoScroll();
    startAutoScroll();

    return () => {
      stopAutoScroll();
      clearWheelResumeTimeout();
      if (wheelGestureEndTimeoutIdRef.current) {
        clearTimeout(wheelGestureEndTimeoutIdRef.current);
      }
    };
  }, [
    clearWheelResumeTimeout,
    getLoopDistance,
    isDesktopSize,
    normalizeScrollLeft,
    reverse,
    startAutoScroll,
    stopAutoScroll,
  ]); // Add isDesktopSize as dependency to reset on resize since it was causing carousel to stop scrolling

  // Shared release path for the container's own pointerup/pointercancel AND the
  // window-level fallback below, so a stream stolen by the browser mid-drag still
  // resumes autoscroll instead of leaving the carousel stuck paused.
  const finishDrag = useCallback(
    (pointerId: number) => {
      const scrollContainer = logoContainerRef.current;
      if (
        !isPointerDownRef.current ||
        activePointerIdRef.current !== pointerId
      ) {
        return;
      }

      isPointerDownRef.current = false;
      isDraggingRef.current = false;
      activePointerIdRef.current = null;
      if (scrollContainer?.hasPointerCapture(pointerId)) {
        scrollContainer.releasePointerCapture(pointerId);
      }
      startAutoScroll();
    },
    [startAutoScroll]
  );

  useEffect(() => {
    const onWindowPointerEnd = (event: globalThis.PointerEvent) => {
      finishDrag(event.pointerId);
    };

    window.addEventListener("pointerup", onWindowPointerEnd);
    window.addEventListener("pointercancel", onWindowPointerEnd);
    return () => {
      window.removeEventListener("pointerup", onWindowPointerEnd);
      window.removeEventListener("pointercancel", onWindowPointerEnd);
    };
  }, [finishDrag]);

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const scrollContainer = logoContainerRef.current;
      if (!scrollContainer) return;

      clearWheelResumeTimeout();
      stopAutoScroll();
      isPointerDownRef.current = true;
      isDraggingRef.current = false;
      suppressClickRef.current = false;
      activePointerIdRef.current = event.pointerId;
      dragStartXRef.current = event.clientX;
      dragStartScrollLeftRef.current = scrollContainer.scrollLeft;
    },
    [clearWheelResumeTimeout, stopAutoScroll]
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const scrollContainer = logoContainerRef.current;
      if (!scrollContainer || !isPointerDownRef.current) return;

      const dragDistance = event.clientX - dragStartXRef.current;
      if (
        !isDraggingRef.current &&
        Math.abs(dragDistance) < DRAG_START_THRESHOLD_PX
      ) {
        return;
      }

      if (!isDraggingRef.current) {
        isDraggingRef.current = true;
        suppressClickRef.current = true;
        scrollContainer.setPointerCapture(event.pointerId);
      }

      scrollContainer.scrollLeft = normalizeScrollLeft(
        scrollContainer,
        dragStartScrollLeftRef.current - dragDistance
      );
    },
    [normalizeScrollLeft]
  );

  const handlePointerEnd = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      finishDrag(event.pointerId);
    },
    [finishDrag]
  );

  const handleClickCapture = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!suppressClickRef.current) return;

      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
    },
    []
  );

  // How long a gap between wheel events ends a gesture burst, so the next one
  // re-evaluates its own axis from scratch rather than inheriting a stale commitment.
  const WHEEL_GESTURE_GAP_MS = 150;

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      const scrollContainer = logoContainerRef.current;
      if (!scrollContainer || isDraggingRef.current) return;

      const horizontalDelta = event.deltaX;
      if (!horizontalDelta && !event.deltaY) return;

      if (wheelGestureAxisRef.current === null) {
        wheelGestureAxisRef.current =
          Math.abs(horizontalDelta) > Math.abs(event.deltaY)
            ? "horizontal"
            : "vertical";
      }

      if (wheelGestureEndTimeoutIdRef.current) {
        clearTimeout(wheelGestureEndTimeoutIdRef.current);
      }
      wheelGestureEndTimeoutIdRef.current = setTimeout(() => {
        wheelGestureAxisRef.current = null;
        wheelGestureEndTimeoutIdRef.current = null;
      }, WHEEL_GESTURE_GAP_MS);

      if (wheelGestureAxisRef.current !== "horizontal" || !horizontalDelta) {
        return;
      }

      event.preventDefault();
      clearWheelResumeTimeout();
      stopAutoScroll();

      scrollContainer.scrollLeft = normalizeScrollLeft(
        scrollContainer,
        scrollContainer.scrollLeft + horizontalDelta
      );

      wheelResumeTimeoutIdRef.current = setTimeout(() => {
        wheelResumeTimeoutIdRef.current = null;
        startAutoScroll();
      }, 500);
    },
    [
      clearWheelResumeTimeout,
      normalizeScrollLeft,
      startAutoScroll,
      stopAutoScroll,
    ]
  );

  useEffect(() => {
    const scrollContainer = logoContainerRef.current;
    if (!scrollContainer) return;

    scrollContainer.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel]);

  const renderCarouselItem = useCallback(
    (entry: CarouselEntry, key: string) => {
      const creatorUsername = entry.username;
      return (
        <div
          className="ColumnNormal"
          key={key}
          style={{ width: isDesktopSize ? 230 : 160, padding: 5, gap: 14 }}
        >
          <img
            src={entry.image}
            alt={entry.title}
            draggable={false}
            style={{
              height: isDesktopSize ? 230 : 160,
              width: isDesktopSize ? 230 : 160,
              borderRadius: 8,
              cursor: creatorUsername ? "pointer" : "auto",
            }}
            onClick={() => {
              if (creatorUsername) {
                window.open(
                  `${getShareDomain()}/u/${creatorUsername}`,
                  "_blank"
                );
              }
            }}
          />
          <div
            className="ColumnCentering"
            style={{ gap: 4, height: isDesktopSize ? 56 : 42 }}
          >
            <span
              className="sectionTitle"
              style={{ fontSize: isDesktopSize ? 16 : 12 }}
            >
              {entry.title}
            </span>
            <span
              className="smallBodySubtext"
              style={{
                whiteSpace: "pre-line",
                textAlign: "center",
                fontSize: isDesktopSize ? 12 : 9,
              }}
            >
              {entry.subtext ?? ""}
            </span>
          </div>
        </div>
      );
    },
    [isDesktopSize]
  );

  return (
    <div
      ref={logoContainerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onClickCapture={handleClickCapture}
      style={{
        display: "flex",
        gap: 14,
        overflow: "hidden",
        width: "100%",
        touchAction: "pan-y",
        overscrollBehaviorX: "contain",
        userSelect: "none",
      }}
    >
      {[...Array(2)].map((_, groupIndex) => (
        <div
          key={groupIndex}
          className="AlignedRow"
          style={{ gap: isDesktopSize ? 14 : 10, flexShrink: 0, zIndex: 1 }}
        >
          {entries.map((entry, index) =>
            renderCarouselItem(entry, `${groupIndex}-${index}`)
          )}
        </div>
      ))}
    </div>
  );
};

type CustomerLogoCarouselProps = {
  isDesktopSize: boolean;
};

const CustomerLogoCarousel = (props: CustomerLogoCarouselProps) => {
  const { isDesktopSize } = props;

  return (
    <div
      className="ColumnCentering"
      style={{ gap: isDesktopSize ? 20 : 14, width: "100%" }}
    >
      <span
        className="sectionTitle"
        style={{ fontSize: isDesktopSize ? 16 : 14, color: Colors.GRAY1 }}
      >
        Trusted by 300+ creators, brands, and businesses
      </span>
      <div
        className="ColumnCentering"
        style={{ gap: isDesktopSize ? 30 : 14, width: "100%" }}
      >
        <div style={{ position: "relative", width: "100%" }}>
          {isDesktopSize ? (
            <>
              {/* Left fade overlay */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 120,
                  background:
                    "linear-gradient(to right, rgba(0, 0, 0, 0.9), transparent)",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />
              {/* Right fade overlay */}
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 120,
                  background:
                    "linear-gradient(to left, rgba(0, 0, 0, 0.9), transparent)",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />
            </>
          ) : null}
          <CarouselRow
            entries={TOP_ROW_ENTRIES}
            isDesktopSize={isDesktopSize}
          />
        </div>
        <div style={{ position: "relative", width: "100%" }}>
          {isDesktopSize ? (
            <>
              {/* Left fade overlay */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 120,
                  background:
                    "linear-gradient(to right, rgba(0, 0, 0, 0.9), transparent)",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />
              {/* Right fade overlay */}
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 120,
                  background:
                    "linear-gradient(to left, rgba(0, 0, 0, 0.9), transparent)",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              />
            </>
          ) : null}
          <CarouselRow
            entries={REVERSED_BOTTOM_ROW_ENTRIES}
            isDesktopSize={isDesktopSize}
            reverse
          />
        </div>
      </div>
    </div>
  );
};

export default memo(CustomerLogoCarousel);
